import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  handleGetSharingGeometriesGeometryIdSharingGetQueryKey,
  handleGetSharingStudiesStudyIdSharingGetQueryKey,
} from '@/api/generated/@tanstack/react-query.gen';
import type { SharingSettingsResponse } from '@/api/generated/types.gen';
import {
  useRecentViewIdentity,
  type RecentViewIdentity,
} from '@/hooks/useRecentViewIdentity';
import { recordRecentView, type RecentViewKind } from '@/utils/recentlyViewed';

interface RecordRecentViewArgs {
  kind: RecentViewKind;
  /** Resource id, or null while the route hasn't resolved one. */
  id: string | null;
  /** Display name, or null until the entity loads. */
  name: string | null | undefined;
  /** Study status, so the row can open on the right landing route. */
  status?: string | null;
  /**
   * `my_role` from the geometry/study response. `'owner'` skips the write —
   * owned items already have their own list. A MISSING role still records:
   * `shareAccess.ts` treats absent as least privilege, and an anonymous share
   * visitor holds no role at all yet is exactly who this list is for.
   */
  myRole: string | null | undefined;
}

/**
 * Record this page view into the per-user recently-viewed list when the item
 * isn't the viewer's own (ENG-2009). Writes to `localStorage`; no request.
 *
 * Mounted on all four detail routes — both `_app` routes and both `/share`
 * routes — because roles get bounced between them: `_app/meshgen/$uuid`
 * redirects below-editor roles out to `/share/geometry/$uuid`, and
 * `/share/study/$studyId` auto-forwards known signed-in roles into
 * `_app/simops/$studyId`. De-duping on `(kind, id)` makes the double write
 * across a redirect a no-op.
 *
 * Returns the identity it resolved, so a caller that also needs the bucket
 * (the revoked-share "Remove" action) reuses this one rather than mounting a
 * second `useAuth` observer on the app's two busiest routes.
 */
export function useRecordRecentView({
  kind,
  id,
  name,
  status,
  myRole,
}: RecordRecentViewArgs): RecentViewIdentity {
  const identity = useRecentViewIdentity();
  const { userId, ready } = identity;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!id || !name) return;
    if (myRole === 'owner') return;
    // Which bucket this browser belongs to is not always known yet, and a
    // wrong guess files a signed-in user's view where no screen will read it.
    // See `useRecentViewIdentity` for the three states this covers.
    if (!ready) return;

    // Owner, only if a sharing payload is ALREADY cached — never fetched.
    // `/sharing` is owner-only, so for the viewers this list serves this is
    // normally undefined; it fills in for free if that ever changes.
    const sharingKey =
      kind === 'geometry'
        ? handleGetSharingGeometriesGeometryIdSharingGetQueryKey({
            path: { geometry_id: id },
          })
        : handleGetSharingStudiesStudyIdSharingGetQueryKey({
            path: { study_id: id },
          });
    const ownerLabel =
      queryClient.getQueryData<SharingSettingsResponse>(sharingKey)
        ?.owner_email ?? undefined;

    recordRecentView(userId, {
      id,
      kind,
      name,
      status: status ?? undefined,
      ownerLabel,
      viewedAt: new Date().toISOString(),
    });
    // `name` is a string, so the study route's 5s poll re-rendering with the
    // same name doesn't re-run this — only a real rename does, which is the
    // one case where refreshing the stored name is right.
  }, [kind, id, name, status, myRole, userId, ready, queryClient]);

  return identity;
}
